import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { 
  LayoutDashboard, Map as MapIcon, Settings, PlayCircle, 
  Save as SaveIcon, Trash2, Cpu, ArrowLeft, FileText, 
  Layers, Building2, Eye, Printer, Check,
  Package, Box, Truck, ChevronRight
} from 'lucide-react';
import MapEditor from '../components/agv/MapEditor';
import Configuration from '../components/agv/Configuration';
import SimulationView from '../components/agv/SimulationView';
import Dashboard from '../components/agv/Dashboard';
import ProposalHub from '../components/agv/ProposalHub';
import ProposalDocumentView from '../components/agv/ProposalDocumentView';

const WORKFLOW_STEPS = [
  {
    step: 1,
    id: 'layout_racking',
    tab: 'setup',
    section: 'layout',
    title: '1. ผัง, พาเลท & ออกแบบแร็ค',
    subtitle: 'แปลนอาคาร สเกล สเปกพาเลท และวางระบบแร็ค',
    icon: Box,
    badge: 'Layout & Racks',
    color: '#2563eb',
    bgLight: 'bg-blue-50',
    borderColor: 'border-blue-200',
    textColor: 'text-blue-700',
    image: '/images/racks/selective_rack.jpg',
  },
  {
    step: 2,
    id: 'fleet',
    tab: 'config',
    section: null,
    title: '2. เลือกรุ่นรถยก Toyota',
    subtitle: 'เลือกจาก 190 รุ่น คำนวณขนาดช่องวิ่ง & ฟลีท',
    icon: Truck,
    badge: 'Toyota Forklifts',
    color: '#ea580c',
    bgLight: 'bg-orange-50',
    borderColor: 'border-orange-200',
    textColor: 'text-orange-700',
    image: null,
  },
  {
    step: 3,
    id: 'simulation',
    tab: 'simulation',
    section: null,
    title: '3. จำลองทราฟฟิก (Simulation)',
    subtitle: 'คำนวณ Throughput, เวลารอบ Pick/Put 2D/3D',
    icon: PlayCircle,
    badge: 'Traffic Sim',
    color: '#7c3aed',
    bgLight: 'bg-violet-50',
    borderColor: 'border-violet-200',
    textColor: 'text-violet-700',
    image: null,
  },
  {
    step: 4,
    id: 'proposal',
    tab: 'proposal',
    section: null,
    title: '4. เอกสารข้อเสนอโครงการ',
    subtitle: 'สรุปผล สเปก ความคุ้มค่า และพิมพ์ PDF',
    icon: FileText,
    badge: 'Proposal PDF',
    color: '#e11d48',
    bgLight: 'bg-rose-50',
    borderColor: 'border-rose-200',
    textColor: 'text-rose-700',
    image: null,
  }
];

const DEFAULT_MAP_DATA = {
  nodes: [], edges: [], areas: [], corridors: [], flows: [],
  palletSize: { width: 1000, length: 1200, height: 150 },
  palletTypes: [
    { id: 'pt-1', name: 'Standard Pallet', width: 1000, length: 1200, height: 1000, color: '#0ea5e9' }
  ],
  bgImageData: null,
  scaleCalibration: { point1: null, point2: null, distanceMm: '', pixelsPerMm: 0.0125 },
};

const DEFAULT_CONFIG = {
  fleetSize: 3, agvWidth: 1000, agvLength: 1600, agvSpeed: 2.0,
  orderRate: 20, storageLogic: 'front-to-back', agvColor: '#c8102e',
};

export default function SiteSurveySimulation() {
  // 'hub' = Gallery & Case Studies | 'editor' = Map & Simulation Editor
  const [viewMode, setViewMode] = useState('hub');
  const [activeTab, setActiveTab] = useState('setup');
  const [activeEditorSection, setActiveEditorSection] = useState('layout');

  // Currently opened proposal
  const [activeProposal, setActiveProposal] = useState(null);
  const [selectedForklift, setSelectedForklift] = useState(null);

  // Map & Simulation State
  const [mapData, setMapData] = useState(DEFAULT_MAP_DATA);
  const [config, setConfig] = useState(DEFAULT_CONFIG);
  const [metricsHistory, setMetricsHistory] = useState([]);
  const [saveStatus, setSaveStatus] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  // Load a specific proposal and switch to Editor
  const handleOpenProposal = async (proposalId, targetTab = 'setup') => {
    setIsLoading(true);
    try {
      const res = await fetch(`/api/proposals/${proposalId}`);
      const json = await res.json();
      if (json.status === 'success' && json.data) {
        const p = json.data;
        setActiveProposal(p);

        // Load Map Data
        if (p.map_data && Object.keys(p.map_data).length > 0) {
          setMapData(p.map_data);
        } else {
          setMapData(DEFAULT_MAP_DATA);
        }

        // Load Fleet Config
        if (p.fleet_config && Object.keys(p.fleet_config).length > 0) {
          setConfig(prev => ({ ...prev, ...p.fleet_config }));
        } else {
          setConfig(DEFAULT_CONFIG);
        }

        // Load Selected Forklift
        if (p.forklift_config && Object.keys(p.forklift_config).length > 0) {
          setSelectedForklift(p.forklift_config);
        } else {
          setSelectedForklift(null);
        }

        setActiveTab(targetTab || 'setup');
        setViewMode('editor');
      }
    } catch (err) {
      console.error('Failed to load proposal:', err);
      alert('ไม่สามารถโหลดข้อมูล Proposal ได้');
    } finally {
      setIsLoading(false);
    }
  };

  // Support direct URL query parameter linking (e.g. ?proposal=1&tab=config or ?tab=config)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const pId = params.get('proposal');
    const tab = params.get('tab');
    if (pId) {
      handleOpenProposal(pId, tab || 'setup');
    } else if (tab) {
      setViewMode('editor');
      setActiveTab(tab);
    }
  }, []);

  // Save current proposal
  const handleSaveProposal = async () => {
    setSaveStatus('Saving...');
    try {
      const now = new Date();
      const dateStr = now.toLocaleDateString('th-TH', { 
        day: 'numeric', 
        month: 'short', 
        year: '2-digit', 
        hour: '2-digit', 
        minute: '2-digit' 
      });

      if (!activeProposal) {
        // Create new proposal directly in Database
        const payload = {
          title: `ผังจำลองคลังสินค้า (${dateStr})`,
          customer_name: 'แบบร่างข้อเสนอ',
          industry: 'general',
          status: 'draft',
          selected_forklift_id: selectedForklift?.id || null,
          forklift_config: selectedForklift || {},
          fleet_config: config,
          map_data: mapData,
          proposal_summary: {
            recommended_fleet: config.fleetSize,
          }
        };

        const res = await fetch('/api/proposals', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
        const data = await res.json();
        if (data.status === 'success' && data.data) {
          setActiveProposal(data.data);
          setSaveStatus('Saved!');
        } else {
          setSaveStatus('Error');
        }
      } else {
        // Update existing proposal in Database
        const payload = {
          title: activeProposal.title,
          customer_name: activeProposal.customer_name,
          industry: activeProposal.industry,
          status: activeProposal.status,
          selected_forklift_id: selectedForklift?.id || activeProposal.selected_forklift_id,
          forklift_config: selectedForklift || activeProposal.forklift_config,
          fleet_config: config,
          map_data: mapData,
          proposal_summary: {
            ...(activeProposal.proposal_summary || {}),
            recommended_fleet: config.fleetSize,
          }
        };

        const res = await fetch(`/api/proposals/${activeProposal.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
        const data = await res.json();
        if (data.status === 'success') {
          setActiveProposal(prev => ({ ...prev, ...payload }));
          setSaveStatus('Saved!');
        } else {
          setSaveStatus('Error');
        }
      }
    } catch (err) {
      console.error('Error saving proposal:', err);
      setSaveStatus('Error');
    }
    setTimeout(() => setSaveStatus(''), 3000);
  };

  const handleClearProject = () => {
    if (!window.confirm('คุณแน่ใจหรือไม่ว่าต้องการล้างข้อมูลผังทั้งหมด? ข้อมูลที่ไม่ได้บันทึกจะสูญหาย')) return;
    setMapData(DEFAULT_MAP_DATA);
    setConfig(DEFAULT_CONFIG);
    setMetricsHistory([]);
    setActiveTab('setup');
  };

  const handleBackToHub = () => {
    setViewMode('hub');
    setActiveProposal(null);
  };

  /* ─── Content Rendering ────────────────────────────────────────── */
  const renderContent = () => {
    if (viewMode === 'hub') {
      return (
        <ProposalHub
          onOpenProposal={handleOpenProposal}
        />
      );
    }

    if (isLoading) {
      return (
        <div className="flex flex-col items-center justify-center h-full gap-3 text-gray-400 py-24">
          <Cpu size={40} className="animate-pulse text-red-500" />
          <p className="text-sm font-medium">กำลังโหลดข้อมูลผังและสเปก...</p>
        </div>
      );
    }

    switch (activeTab) {
      case 'setup':
        return (
          <MapEditor 
            mapData={mapData} 
            setMapData={setMapData} 
            activeSection={activeEditorSection}
            setActiveSection={setActiveEditorSection}
            onNavigateTab={(tab) => {
              if (tab === 'config') setActiveTab('config');
              else if (tab === 'simulation') setActiveTab('simulation');
              else if (tab === 'proposal') setActiveTab('proposal');
            }}
          />
        );
      case 'config':
        return (
          <Configuration
            config={config}
            setConfig={setConfig}
            mapData={mapData}
            selectedForklift={selectedForklift}
            setSelectedForklift={setSelectedForklift}
            onNavigateTab={(tab) => {
              if (tab === 'setup') setActiveTab('setup');
              else if (tab === 'simulation') setActiveTab('simulation');
              else if (tab === 'proposal') setActiveTab('proposal');
            }}
          />
        );
      case 'simulation':
        return (
          <SimulationView
            mapData={mapData}
            config={config}
            setMetricsHistory={setMetricsHistory}
          />
        );
      case 'dashboard':
        return <Dashboard metricsHistory={metricsHistory} />;
      case 'proposal':
        return (
          <ProposalDocumentView
            proposal={activeProposal}
            mapData={mapData}
            config={config}
            selectedForklift={selectedForklift}
            onBack={() => setActiveTab('setup')}
            onUpdateProposal={(updatedData) => {
              setActiveProposal(prev => ({ ...prev, ...updatedData }));
            }}
          />
        );
      default:
        return null;
    }
  };

  /* ─── Workflow Step Progression Helpers ────────────────────────── */
  const getCurrentStepIndex = () => {
    if (activeTab === 'config') return 2;
    if (activeTab === 'simulation') return 3;
    if (activeTab === 'proposal') return 4;
    return 1; // 'setup' tab combines layout, pallets, and racks
  };

  const isStepDone = (stepNum) => {
    switch (stepNum) {
      case 1:
        return !!(mapData.bgImageData || mapData.svgContent || mapData.blankWarehouse) ||
               (mapData.areas || []).some(a => a.type?.startsWith('storage'));
      case 2:
        return !!selectedForklift || !!activeProposal?.selected_forklift_id;
      case 3:
        return (metricsHistory && metricsHistory.length > 0);
      case 4:
        return !!activeProposal?.proposal_summary;
      default:
        return false;
    }
  };

  const handleStepClick = (stepItem) => {
    if (stepItem.tab === 'setup') {
      setActiveTab('setup');
      if (stepItem.section) {
        setActiveEditorSection(stepItem.section);
      }
    } else {
      setActiveTab(stepItem.tab);
    }
  };

  /* ─── Header Content ───────────────────────────────────────────── */
  const headerContent = (
    <>
      {viewMode === 'editor' ? (
        <>
          {/* Left: Back to Hub + Proposal Title info */}
          <div className="flex items-center gap-2 shrink-0">
            <button
              onClick={handleBackToHub}
              className="inline-flex items-center gap-1.5 px-2.5 py-1.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg text-xs font-semibold transition-colors cursor-pointer"
            >
              <ArrowLeft size={14} />
              คลัง Proposal
            </button>

            <div className="flex items-center gap-1.5 bg-gray-50 border border-gray-200 px-2 py-1 rounded-lg">
              <span className="text-xs font-bold text-gray-900 truncate max-w-[140px]" title={activeProposal?.title}>
                {activeProposal?.title || 'ข้อเสนอโครงการ'}
              </span>
              <span className="text-[10px] text-gray-300">/</span>
              <span className="text-[11px] text-gray-500 truncate max-w-[120px]">
                {activeProposal?.customer_name || 'ลูกค้า'}
              </span>
            </div>
          </div>

          {/* Center: Stage breadcrumb / indicator */}
          <div className="hidden md:flex items-center gap-2 px-3 py-1 bg-red-50/80 border border-red-100 rounded-lg">
            <span className="w-2 h-2 rounded-full bg-red-600 animate-pulse shrink-0" />
            <span className="text-xs font-bold text-red-950">
              Toyota Solution Configurator
            </span>
            <span className="text-[10px] text-gray-300">|</span>
            <span className="text-xs text-red-700 font-medium truncate max-w-[260px]">
              ขั้นตอนที่ {getCurrentStepIndex()}/4: {WORKFLOW_STEPS.find(s => s.step === getCurrentStepIndex())?.title}
            </span>
          </div>

          <div className="flex-1 min-w-[8px]" />

          {/* Right Actions */}
          <div className="flex items-center gap-2 shrink-0">
            {saveStatus && (
              <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                saveStatus === 'Saved!' ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' :
                saveStatus === 'Error' ? 'bg-red-50 text-red-600 border border-red-200' :
                'bg-gray-50 text-gray-500'
              }`}>
                {saveStatus}
              </span>
            )}
            <button
              onClick={handleClearProject}
              title="ล้างข้อมูลผัง"
              className="inline-flex items-center gap-1 px-2.5 py-1.5 bg-white border border-gray-200 text-gray-600 hover:border-red-300 hover:text-red-600 hover:bg-red-50 rounded-lg text-xs font-medium transition-colors cursor-pointer"
            >
              <Trash2 size={13} /> ล้างผัง
            </button>
            <button
              onClick={handleSaveProposal}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-red-600 hover:bg-red-700 text-white rounded-lg text-xs font-bold transition-colors shadow-xs cursor-pointer"
            >
              <SaveIcon size={13} /> บันทึก Proposal
            </button>
          </div>
        </>
      ) : (
        <div className="flex items-center justify-between w-full py-1">
          <div className="flex items-center gap-2.5">
            <div className="w-7 h-7 bg-red-600 text-white rounded-lg flex items-center justify-center font-black text-xs shadow-xs">
              <Layers size={16} />
            </div>
            <div>
              <span className="font-extrabold text-gray-900 text-sm">Solution Proposal Hub</span>
              <span className="hidden sm:inline-block ml-2 text-xs text-gray-400">| Toyota Material Handling Thailand</span>
            </div>
          </div>
          <span className="text-xs text-gray-500 bg-gray-50 px-2.5 py-1 rounded-full border border-gray-200">
            Toyota Specification 2026 Engine
          </span>
        </div>
      )}
    </>
  );

  return (
    <div className="flex flex-col h-full" style={{ minHeight: 'calc(100vh - 64px)' }}>
      {document.getElementById('topbar-portal-target') 
        ? createPortal(headerContent, document.getElementById('topbar-portal-target'))
        : <div className="bg-white border-b border-gray-200 px-4 py-2 flex items-center justify-between shrink-0 gap-4 overflow-x-auto">{headerContent}</div>
      }

      {/* 2nd Row: Dedicated Workflow Progression Stepper with Graphics and Thai Descriptions */}
      {viewMode === 'editor' && (
        <div className="bg-white border-b border-gray-200 px-3 py-2 shadow-xs shrink-0 select-none overflow-x-auto">
          <div className="flex items-center gap-1.5 min-w-max mx-auto max-w-7xl">
            {WORKFLOW_STEPS.map((s, idx) => {
              const isCurrent = getCurrentStepIndex() === s.step;
              const isDone = isStepDone(s.step);
              const StepIcon = s.icon;

              return (
                <React.Fragment key={s.step}>
                  <button
                    type="button"
                    onClick={() => handleStepClick(s)}
                    className={`flex items-center gap-2 px-2.5 py-1.5 rounded-xl text-left transition-all cursor-pointer border ${
                      isCurrent
                        ? 'bg-red-50/90 border-red-500 shadow-sm ring-2 ring-red-100'
                        : isDone
                        ? 'bg-white border-emerald-200 hover:border-emerald-300 hover:bg-emerald-50/30'
                        : 'bg-white border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                    }`}
                    style={{ minWidth: '210px', maxWidth: '300px' }}
                  >
                    {/* Graphic / Thumbnail */}
                    <div className="relative shrink-0">
                      {s.image ? (
                        <div className={`w-8 h-8 rounded-lg overflow-hidden border ${isCurrent ? 'border-red-400 ring-2 ring-red-300' : 'border-gray-200'} bg-gray-50`}>
                          <img src={s.image} alt="" className="w-full h-full object-cover" />
                        </div>
                      ) : (
                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center font-black text-xs border ${
                          isCurrent
                            ? 'bg-red-600 text-white border-red-600 shadow-xs'
                            : isDone
                            ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                            : `${s.bgLight} ${s.textColor} ${s.borderColor}`
                        }`}>
                          <StepIcon size={16} />
                        </div>
                      )}
                      {isDone && !isCurrent && (
                        <div className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-emerald-600 text-white flex items-center justify-center text-[10px] font-bold shadow-xs">
                          ✓
                        </div>
                      )}
                    </div>

                    {/* Step Title & Subtitle */}
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-1">
                        <span className={`text-[9px] font-black px-1 py-0.5 rounded leading-none ${
                          isCurrent ? 'bg-red-600 text-white' : isDone ? 'bg-emerald-100 text-emerald-800' : 'bg-gray-100 text-gray-600'
                        }`}>
                          {s.step}
                        </span>
                        <span className={`text-xs font-bold truncate ${isCurrent ? 'text-red-950 font-black' : 'text-gray-800'}`}>
                          {s.title}
                        </span>
                      </div>
                      <div className={`text-[10px] truncate mt-0.5 ${isCurrent ? 'text-red-700 font-semibold' : 'text-gray-500'}`}>
                        {s.subtitle}
                      </div>
                    </div>
                  </button>

                  {idx < WORKFLOW_STEPS.length - 1 && (
                    <ChevronRight size={14} className="text-gray-300 shrink-0" />
                  )}
                </React.Fragment>
              );
            })}
          </div>
        </div>
      )}

      {/* Main Content Area */}
      <div className="flex-1 overflow-auto bg-gray-50">
        <div className="h-full">
          {renderContent()}
        </div>
      </div>
    </div>
  );
}
